import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { paymentStatus } from "../lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { unlock } = useApp();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await paymentStatus(sid);
        if (res.payment_status === "paid") { await unlock(); setState("paid"); return; }
        if (res.status === "expired" || res.payment_status === "failed") { setState("error"); return; }
      } catch (e) {
        console.error("Payment status check failed, will retry:", e);
      }
      if (attempts.current >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, unlock]);

  return (
    <div className="max-w-md mx-auto px-5 py-20 text-center">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 size={40} className="animate-spin mx-auto" color="#2563EB" />
          <p className="mt-4 font-semibold" style={{ color: "#1E293B" }}>Confirming your payment…</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 size={56} className="mx-auto" color="#10B981" />
          <h1 className="text-2xl font-black mt-4" style={{ color: "#1E293B" }}>You're unlocked forever!</h1>
          <p className="mt-2" style={{ color: "#475569" }}>Thank you for supporting Acuman. Enjoy unlimited sessions.</p>
          <Link to="/ask" className="inline-block mt-6 h-11 px-6 rounded-xl text-sm font-bold text-white leading-[44px]" style={{ background: "#2563EB" }}>
            Start asking
          </Link>
        </div>
      )}
      {(state === "error" || state === "timeout") && (
        <div data-testid="payment-error">
          <XCircle size={56} className="mx-auto" color="#EF4444" />
          <h1 className="text-2xl font-black mt-4" style={{ color: "#1E293B" }}>Payment not confirmed</h1>
          <p className="mt-2" style={{ color: "#475569" }}>
            {state === "timeout" ? "It's taking longer than expected. If you were charged, your unlock will apply shortly." : "We couldn't confirm your payment."}
          </p>
          <Link to="/settings" className="inline-block mt-6 h-11 px-6 rounded-xl text-sm font-bold leading-[44px] border" style={{ borderColor: "#CBD5E1", color: "#1E293B" }}>
            Back to Settings
          </Link>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-md mx-auto px-5 py-20 text-center" data-testid="payment-cancel">
      <XCircle size={56} className="mx-auto" color="#94A3B8" />
      <h1 className="text-2xl font-black mt-4" style={{ color: "#1E293B" }}>Checkout cancelled</h1>
      <p className="mt-2" style={{ color: "#475569" }}>No charge was made. You can unlock anytime.</p>
      <Link to="/ask" className="inline-block mt-6 h-11 px-6 rounded-xl text-sm font-bold text-white leading-[44px]" style={{ background: "#2563EB" }}>
        Back to Ask
      </Link>
    </div>
  );
}
